// DiffPage — /studio/:slug/:repo/_/diff?from=&to=
// Shows two RevPicker controls for selecting before/after refs and renders
// DiffBoard when the diff data has loaded.

import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { RevPicker } from "./RevPicker.js";
import { useGitDiff } from "./useGitDiff.js";
import { useRepoRefs } from "./useRepoRefs.js";
import { DiffBoard } from "./DiffBoard.js";
import { DiffPackageTabs } from "./DiffPackageTabs.js";
import { DiffDiagnostics } from "./DiffDiagnostics.js";
import { Button } from "../ui/Button.js";
import { Spinner } from "../ui/Spinner.js";
import { Banner } from "../ui/Banner.js";
import { useToast } from "../ui/Toast.js";
import { useTranslation } from "../i18n";
import "./diff.css";

export function DiffPage() {
  const { t } = useTranslation("diff");
  const { slug = "", repo = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const refsQuery = useRepoRefs({ slug, repo });
  // The server has no defaultBranch field; fall back to the first branch (then
  // "main") so a repo whose trunk isn't "main" doesn't diff a nonexistent ref.
  const fallbackRef = refsQuery.data?.branches[0] ?? "main";
  const from = search.get("from") ?? fallbackRef;
  const to   = search.get("to")   ?? fallbackRef;
  const diff = useGitDiff({ slug, repo, from, to });
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const pkgs = diff.data?.packages ?? [];
  const active = pkgs.find((p) => p.key === activeKey) ?? pkgs[0];

  const toast = useToast();
  const setFrom = (v: string) => setSearch((s) => { s.set("from", v); return s; });
  const setTo   = (v: string) => setSearch((s) => { s.set("to",   v); return s; });

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 bg-slate-950 px-4 py-2.5">
        <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
          {t("page.from")}
          <RevPicker slug={slug} repo={repo} value={from} onChange={setFrom} />
        </label>
        <svg className="h-4 w-4 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
          {t("page.to")}
          <RevPicker slug={slug} repo={repo} value={to} onChange={setTo} />
        </label>
        <div className="ml-auto">
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await navigator.clipboard.writeText(location.href);
              toast.success(t("page.linkCopied"));
            }}
          >
            {t("page.copyLink")}
          </Button>
        </div>
      </div>
      {diff.isLoading && (
        <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-400">
          <Spinner /> {t("page.computing")}
        </div>
      )}
      {diff.error && (
        <div className="p-6">
          <Banner kind="error">{String(diff.error)}</Banner>
        </div>
      )}
      {diff.data && !active && (
        <div className="flex items-center justify-center py-16 text-sm text-slate-500">
          {t("page.noChanges")}
        </div>
      )}
      {diff.data && active && (
        <div className="flex min-h-0 flex-1 flex-col">
          <DiffPackageTabs packages={pkgs} activeKey={active.key} onSelect={setActiveKey} />
          <DiffDiagnostics diagnostics={active.diagnostics} />
          <div className="min-h-0 flex-1">
            <DiffBoard
              beforeMods={active.before.model.modules}
              afterMods={active.after.model.modules}
              beforeEdges={active.before.edges}
              afterEdges={active.after.edges}
              changeSet={active.changeSet}
              slug={slug}
              repo={repo}
              toRef={to}
            />
          </div>
        </div>
      )}
    </div>
  );
}
